/**
 * Operational health.
 *
 * # What this dashboard is for
 *
 * Not CPU, memory, and disk. Those belong to whatever monitors the box, and they are green
 * throughout every failure this platform actually has. A platform whose worst outcome is
 * "somebody holds standing access into a client's estate" needs a screen that answers:
 *
 * - Is anybody waiting on an approval nobody has looked at?
 * - Is a contract live right now?
 * - Is a Collector that should be reporting silent?
 * - Has anyone been probing what the contract intake will accept?
 *
 * # Tone is derived, not decorative
 *
 * A number is amber or red because acting on it is warranted, never because it is large. Fifty
 * open tickets is a Tuesday; one contract that outlived its revocation is an incident. Colouring
 * by magnitude is how a dashboard becomes wallpaper — everything is always slightly orange, so
 * nobody looks.
 *
 * The refresh is 15 seconds and the timestamp is shown. A tab left open overnight should look
 * stale rather than look calm.
 */

import { useEffect, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { api, type OpsHealth as OpsHealthData } from '../shared/api/client';
import { Card, ErrorBox, PageHeader, Spinner, Stat, StatRow } from '../shared/ui/ui';

const REFRESH_MS = 15_000;

export function OpsHealth(): JSX.Element {
  const [data, setData] = useState<OpsHealthData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [stale, setStale] = useState(false);
  // Both held in refs so the poll closure never captures a stale value and the timer is created
  // exactly once. `hasData` in particular: without it, `data` would have to be a dependency, and
  // the interval would be torn down and rebuilt on every successful poll.
  const cancelled = useRef(false);
  const hasData = useRef(false);

  useEffect(() => {
    cancelled.current = false;

    const poll = async (): Promise<void> => {
      try {
        const next = await api.opsHealth();
        if (!cancelled.current) {
          hasData.current = true;
          setData(next);
          setError(null);
          setStale(false);
        }
      } catch (e: unknown) {
        // The screen that reports outages must degrade during one. Keep the last known values and
        // say plainly that they are old — blanking the page loses the very information somebody
        // opened it for.
        if (!cancelled.current) {
          setStale(true);
          // Only surface an error when there is nothing to show. Once a read has succeeded, a
          // later failure is reported by the stale indicator instead — replacing a screen of live
          // numbers with an error box loses exactly what somebody opened it for.
          if (!hasData.current) setError(e);
        }
      }
    };

    void poll();
    const timer = window.setInterval(() => void poll(), REFRESH_MS);
    return (): void => {
      cancelled.current = true;
      window.clearInterval(timer);
    };
  }, []);

  if (data === null) {
    return (
      <>
        <PageHeader title="Operational health" subtitle="Loading…" />
        <ErrorBox error={error} />
        {error === null && <Spinner />}
      </>
    );
  }

  const { platform, loop_: loop, estate, ledger } = data;

  return (
    <>
      <PageHeader
        title="Operational health"
        subtitle="The loop, the estate, and the ledger. Host vitals belong to whatever monitors the box; nothing here is green while access is going wrong."
      />

      <ErrorBox error={error} />

      <div className="ops__status">
        <span className={stale ? 'ops__dot ops__dot--stale' : 'ops__dot'} aria-hidden="true" />
        <span>
          {stale
            ? 'Cannot reach the platform — showing the last successful read'
            : `live · refreshes every ${String(REFRESH_MS / 1000)}s`}
        </span>
        <span className="ops__asof">as of {clock(data.as_of)}</span>
      </div>

      {/* ------------------------------------------------------------------------------------ */}
      {/* The loop. First, because it is the product.                                          */}
      {/* ------------------------------------------------------------------------------------ */}
      <Card
        title="The loop"
        subtitle="A ticket arrives, access is scoped and dual-approved, the client signs, the session is recorded. Each number below is a place that chain can stall."
      >
        <StatRow>
          <Stat label="Open tickets" value={loop.tickets_open} hint="not resolved or closed" />
          <Stat
            label="Unassigned"
            value={loop.tickets_unassigned}
            tone={loop.tickets_unassigned > 0 ? 'warn' : 'neutral'}
            hint="the quietest way work stalls"
          />
          <Stat
            label="Escalated"
            value={loop.tickets_escalated}
            tone={loop.tickets_escalated > 0 ? 'danger' : 'ok'}
            hint="an SLA escalation was raised"
          />
        </StatRow>

        <StatRow>
          <Stat
            label="Awaiting provider"
            value={loop.awaiting_provider}
            tone={loop.awaiting_provider > 0 ? 'info' : 'neutral'}
            hint="our queue"
          />
          <Stat
            label="Awaiting client"
            value={loop.awaiting_client}
            tone={loop.awaiting_client > 0 ? 'warn' : 'neutral'}
            hint="theirs — this one needs a phone call, not a nudge"
          />
          <Stat
            label="Approved, unsigned"
            value={loop.approved_unsigned}
            tone={loop.approved_unsigned > 0 ? 'warn' : 'neutral'}
            hint="both parties agreed and no contract was filed"
          />
        </StatRow>
      </Card>

      {/* ------------------------------------------------------------------------------------ */}
      {/* Live access. The single most consequential number on the screen.                      */}
      {/* ------------------------------------------------------------------------------------ */}
      <Card
        title="Live access"
        subtitle="Contracts in force at this instant. Every one of these is somebody able to reach a client's estate right now."
      >
        <StatRow>
          <Stat
            label="Live now"
            value={loop.contracts_live}
            // Deliberately `info` rather than `warn`. A live contract is the system working as
            // designed; colouring normal operation as a warning is how people learn to ignore the
            // colour. What warrants attention is the *unexpected* — the two tiles beside it.
            tone={loop.contracts_live > 0 ? 'info' : 'neutral'}
            hint="unrevoked and inside its window"
          />
          <Stat
            label="Expiring within the hour"
            value={loop.contracts_expiring}
            tone={loop.contracts_expiring > 0 ? 'warn' : 'neutral'}
            hint="an engineer is about to lose access mid-task"
          />
          <Stat
            label="Revoked today"
            value={loop.contracts_revoked_today}
            tone={loop.contracts_revoked_today > 2 ? 'warn' : 'neutral'}
            hint="one is routine; a run of them is worth reading"
          />
        </StatRow>
        <p className="ops__note">
          <Link to="/contracts">Open the contract register →</Link>
        </p>
      </Card>

      {/* ------------------------------------------------------------------------------------ */}
      {/* The estate.                                                                           */}
      {/* ------------------------------------------------------------------------------------ */}
      <Card
        title="The estate"
        subtitle="What is under management, and how much of it we can actually see."
      >
        <StatRow>
          <Stat label="Configuration items" value={estate.cis_total} />
          <Stat
            label="Reachable"
            value={estate.cis_reachable}
            hint="most CIs are records, not doors"
          />
          <Stat
            label="Never captured"
            value={estate.cis_never_captured}
            tone={estate.cis_never_captured > 0 ? 'warn' : 'ok'}
            hint="a CMDB gap, not a device fault"
          />
        </StatRow>

        <StatRow>
          <Stat label="Collectors" value={estate.collectors_total} />
          <Stat
            label="Reporting"
            value={estate.collectors_healthy}
            tone={estate.collectors_healthy > 0 ? 'ok' : 'neutral'}
            hint="seen within 15 minutes"
          />
          <Stat
            label="Silent"
            value={estate.collectors_stale}
            tone={estate.collectors_stale > 0 ? 'danger' : 'ok'}
            // The failure that hides itself: drift stops being detected and every other panel
            // goes on looking exactly as it did.
            hint="drift stops being detected, and nothing else changes to tell you"
          />
        </StatRow>

        <StatRow>
          <Stat
            label="Critical findings"
            value={estate.vulns_critical}
            tone={estate.vulns_critical > 0 ? 'danger' : 'ok'}
          />
          <Stat
            label="High findings"
            value={estate.vulns_high}
            tone={estate.vulns_high > 0 ? 'warn' : 'ok'}
          />
        </StatRow>
      </Card>

      {/* ------------------------------------------------------------------------------------ */}
      {/* The ledger.                                                                           */}
      {/* ------------------------------------------------------------------------------------ */}
      <Card
        title="The ledger"
        subtitle="Hash-chained and append-only. It is the answer to a client asking what an engineer did inside their estate."
      >
        <StatRow>
          <Stat label="Entries today" value={ledger.events_today} />
          <Stat label="Entries in total" value={ledger.events_total} />
          <Stat
            label="Last entry"
            value={ledger.last_event_at === null ? '—' : clock(ledger.last_event_at)}
            hint="a ledger that stops growing while people are working is a broken writer"
          />
        </StatRow>

        <StatRow>
          <Stat
            label="Contracts refused today"
            value={ledger.contracts_refused_today}
            // The one count on this screen that exists purely as a tripwire. One refusal is an
            // operator error; a run of them is somebody learning what intake will accept, and that
            // pattern is invisible unless something counts it.
            tone={refusalTone(ledger.contracts_refused_today)}
            hint="a signature that did not match what was approved"
          />
        </StatRow>
        <p className="ops__note">
          <Link to="/audit">Read the ledger →</Link>
        </p>
      </Card>

      {/* ------------------------------------------------------------------------------------ */}
      {/* Platform, last. It is the least interesting thing here.                                */}
      {/* ------------------------------------------------------------------------------------ */}
      <Card title="Platform" subtitle="Dependencies of the control plane itself.">
        <StatRow>
          <Stat
            label="Database"
            value={platform.database ? 'Up' : 'Down'}
            tone={platform.database ? 'ok' : 'danger'}
            hint="PostgreSQL, row-level security forced"
          />
          <Stat
            label="Cache"
            value={platform.cache ? 'Up' : 'Down'}
            tone={platform.cache ? 'ok' : 'danger'}
            hint="Valkey, tenant-prefixed keys"
          />
          <Stat label="Version" value={platform.version} hint="the running build" />
        </StatRow>
      </Card>
    </>
  );
}

/** Zero is fine, a few is an operator learning the tool, many is somebody probing the rules. */
function refusalTone(count: number): 'ok' | 'warn' | 'danger' {
  if (count > 3) return 'danger';
  if (count > 0) return 'warn';
  return 'ok';
}

/** `HH:MM:SS` in the reader's own timezone. */
function clock(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleTimeString();
}
